import React, { useEffect, useRef, useState } from 'react';
import { User, Question, NewsItem, Flashcard, FlashcardDeck, Simulado, Difficulty } from '../types';
import { THEMES, SUBTHEMES_BY_THEME } from '../constants';
import { dbService } from '../services/db';

interface AdminProps {
  user: User | null;
  onNavigate: (page: string) => void;
}

type EditorTab = 'questoes' | 'flashcards' | 'simulados' | 'atualizacoes';

export const Admin: React.FC<AdminProps> = ({ user, onNavigate }) => {
  const [activeTab, setActiveTab] = useState<EditorTab>('questoes');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [isSavingQuestion, setIsSavingQuestion] = useState(false);
  const [isSavingFlashcard, setIsSavingFlashcard] = useState(false);
  const [isSavingSimulado, setIsSavingSimulado] = useState(false);
  const [isSavingNews, setIsSavingNews] = useState(false);

  // Lists
  const [questions, setQuestions] = useState<Question[]>([]);
  const [decks, setDecks] = useState<FlashcardDeck[]>([]);
  const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
  const [simulados, setSimulados] = useState<Simulado[]>([]);
  const [news, setNews] = useState<NewsItem[]>([]);

  const fileInputQuestion = useRef<HTMLInputElement>(null);
  const fileInputFront = useRef<HTMLInputElement>(null);
  const fileInputBack = useRef<HTMLInputElement>(null);

  // Forms
  const [qForm, setQForm] = useState<Partial<Question>>({
    themeId: 1,
    subtheme: '',
    difficulty: 'moderado',
    xpReward: 15,
    correctOption: 'A',
    isPremium: true,
    title: '',
    statement: '',
    explanation: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    imageUrl: '',
  });

  const [fForm, setFForm] = useState<Partial<Flashcard>>({
    xpReward: 5,
    front: '',
    back: '',
    deckId: 1,
    subtheme: '',
    frontImageUrl: '',
    backImageUrl: '',
  });

  const [sForm, setSForm] = useState<Partial<Simulado>>({
    title: '',
    durationMinutes: 60,
    isPremium: true,
    themeId: 1,
    questionIds: [],
  });

  const [nForm, setNForm] = useState<Partial<NewsItem>>({
    themeId: 1,
    isPremium: true,
    title: '',
    content: '',
    summary: '',
    isFixed: false,
  });

  // Filters
  const [qListTheme, setQListTheme] = useState<number>(1);
  const [fListDeck, setFListDeck] = useState<number>(1);

  const authorizedEmails = ['urochamps@gmail.com', 'uroinsights@gmail.com'];

  useEffect(() => {
    if (!user || !authorizedEmails.includes(user.email)) {
      alert('Acesso restrito ao Painel do Editor.');
      onNavigate('dashboard');
    }
  }, [user, onNavigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const qs = await dbService.getQuestions();
        const ds = await dbService.getDecks();
        const fs = await dbService.getFlashcards(fListDeck);
        const ss = await dbService.getAdminSimulados();
        const ns = await dbService.getNews(user || undefined);

        setQuestions(qs);
        setDecks(ds);
        setFlashcards(fs);
        setSimulados(ss);
        setNews(ns);
      } catch (err) {
        console.error('Admin loadData error:', err);
      }
    };

    if (user && authorizedEmails.includes(user.email)) {
      loadData();
    }
  }, [refreshTrigger, fListDeck, user]);

  if (!user || !authorizedEmails.includes(user.email)) return null;

  const refresh = () => setRefreshTrigger(prev => prev + 1);

  const resetForms = () => {
    setEditingId(null);
    setQForm({
      themeId: 1,
      subtheme: '',
      difficulty: 'moderado',
      xpReward: 15,
      correctOption: 'A',
      isPremium: true,
      title: '',
      statement: '',
      explanation: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      imageUrl: '',
    });
    setFForm({ xpReward: 5, front: '', back: '', deckId: 1, subtheme: '', frontImageUrl: '', backImageUrl: '' });
    setSForm({ title: '', durationMinutes: 60, isPremium: true, themeId: 1, questionIds: [] });
    setNForm({ themeId: 1, isPremium: true, title: '', content: '', summary: '', isFixed: false });
  };

  const handleDifficultyChange = (diff: Difficulty) => {
    const xpRewards: Record<Difficulty, number> = {
      fácil: 10,
      moderado: 15,
      difícil: 20,
      hard: 25,
      desafio: 30,
    };
    setQForm({ ...qForm, difficulty: diff, xpReward: xpRewards[diff] });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, field: string, formType: 'q' | 'f') => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('Arquivo muito grande. Limite de 2MB.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (formType === 'q') setQForm(prev => ({ ...prev, [field]: reader.result as string }));
      else setFForm(prev => ({ ...prev, [field]: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const withTimeout = async <T,>(promise: Promise<T>, ms = 15000): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Timeout ao salvar (${ms / 1000}s).`)), ms)
    ),
  ]);
};

const handleSaveQuestion = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!qForm.subtheme) return alert("Selecione um subtema.");

  setIsSavingQuestion(true);
  try {
    if (editingId) {
      await withTimeout(dbService.updateQuestion(editingId, qForm));
    } else {
      await withTimeout(dbService.addQuestion(qForm as Omit<Question, "id">));
    }

    alert(editingId ? "Questão atualizada!" : "Nova questão cadastrada!");
    resetForms();
    refresh();
  } catch (err: any) {
    console.error(err);
    const msg = String(err?.message || "Erro ao salvar questão.");
    alert(msg);
  } finally {
    setIsSavingQuestion(false);
  }
};

  const handleSaveFlashcard = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fForm.subtheme) return alert('Selecione um subtema.');
    if (!String(fForm.front || '').trim()) return alert('Preencha a frente do flashcard.');
    if (!String(fForm.back || '').trim()) return alert('Preencha o verso do flashcard.');

    const deckToShow = fForm.deckId ?? 1;

    setIsSavingFlashcard(true);
    try {
      if (editingId) await dbService.updateFlashcard(editingId, fForm);
      else await dbService.addFlashcard(fForm as Omit<Flashcard, 'id'>);

      setFListDeck(deckToShow);

      alert(editingId ? 'Flashcard atualizado!' : 'Novo flashcard cadastrado!');
      resetForms();
      refresh();
    } catch (err: any) {
      console.error('ERRO save flashcard:', err);
      const msg = err?.message || 'Erro ao salvar flashcard.';
      alert(msg);

      if (String(msg).toLowerCase().includes('refresh token')) {
        alert('Sua sessão expirou. Faça logout/login e tente novamente.');
      }
    } finally {
      setIsSavingFlashcard(false);
    }
  };

  const handleSaveSimulado = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSavingSimulado(true);
    try {
      const ids =
        typeof sForm.questionIds === 'string'
          ? (sForm.questionIds as string)
              .split(',')
              .map(n => parseInt(n.trim()))
              .filter(n => !isNaN(n))
          : sForm.questionIds;

      const data = { ...sForm, questionIds: (ids || []) as number[], authorId: undefined };

      if (editingId) await dbService.updateSimulado(editingId, data as Partial<Simulado>);
      else await dbService.addSimulado(data as Omit<Simulado, 'id'>);

      alert('Simulado salvo!');
      resetForms();
      refresh();
    } catch (err: any) {
      console.error(err);
      alert(err?.message || 'Erro ao salvar simulado.');
    } finally {
      setIsSavingSimulado(false);
    }
  };

  const handleSaveNews = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSavingNews(true);
    try {
      if (editingId) await dbService.updateNews(editingId, nForm);
      else await dbService.addNews(nForm as Omit<NewsItem, 'id' | 'createdAt'>);

      alert('Notícia publicada/atualizada!');
      resetForms();
      refresh();
    } catch (err: any) {
      console.error(err);
      alert(err?.message || 'Erro ao salvar notícia.');
    } finally {
      setIsSavingNews(false);
    }
  };

  const editItem = (type: EditorTab, item: any) => {
    setEditingId(item.id);

    switch (type) {
      case 'questoes':
        setQForm(item);
        break;
      case 'flashcards':
        setFForm(item);
        setFListDeck(item.deckId ?? fListDeck);
        break;
      case 'simulados':
        setSForm({ ...item, questionIds: (item.questionIds?.join(', ') || '') as any });
        break;
      case 'atualizacoes':
        setNForm(item);
        break;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteItem = async (type: EditorTab, id: number) => {
    if (!confirm('Tem certeza que deseja excluir permanentemente este item?')) return;

    try {
      switch (type) {
        case 'questoes':
          await dbService.deleteQuestion(id);
          break;
        case 'flashcards':
          await dbService.deleteFlashcard(id);
          break;
        case 'simulados':
          await dbService.deleteSimulado(id);
          break;
        case 'atualizacoes':
          await dbService.deleteNews(id);
          break;
      }
      refresh();
    } catch (err: any) {
      console.error(err);
      alert(err?.message || 'Erro ao excluir.');
    }
  };

  const stripHtml = (html: string) => {
    try {
      const doc = new DOMParser().parseFromString(html || '', 'text/html');
      return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    } catch {
      return (html || '').replace(/\s+/g, ' ').trim();
    }
  };

  const currentDeck = decks.find(d => d.id === (fForm.deckId || 1));
  const currentThemeIdForSubthemes = currentDeck?.themeId || 1;

  return (
    <div className="min-h-screen bg-[#f0f2f5] font-['Inter'] pb-20">
      <header className="bg-slate-900 py-6 px-8 flex justify-between items-center text-white shadow-xl">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center font-black text-white">UI</div>
          <div>
            <h1 className="font-black text-lg tracking-tight uppercase leading-none">Painel Editor</h1>
            <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mt-1">UroInsights Academy</p>
          </div>
        </div>

        <button
          onClick={() => onNavigate('dashboard')}
          className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-xs font-black uppercase tracking-widest transition-all"
        >
          Sair do Painel
        </button>
      </header>

      <div className="max-w-6xl mx-auto py-8 px-6">
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          <TabButton
            active={activeTab === 'questoes'}
            onClick={() => {
              setActiveTab('questoes');
              resetForms();
            }}
            icon="📝"
            label="Questões"
          />
          <TabButton
            active={activeTab === 'flashcards'}
            onClick={() => {
              setActiveTab('flashcards');
              resetForms();
            }}
            icon="🧠"
            label="Flashcards"
          />
          <TabButton
            active={activeTab === 'simulados'}
            onClick={() => {
              setActiveTab('simulados');
              resetForms();
            }}
            icon="🧩"
            label="Simulados"
          />
          <TabButton
            active={activeTab === 'atualizacoes'}
            onClick={() => {
              setActiveTab('atualizacoes');
              resetForms();
            }}
            icon="📰"
            label="Notícias"
          />
        </div>

        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden p-10">
          {/* ABA QUESTÕES */}
          {activeTab === 'questoes' && (
            <div className="animate-in fade-in duration-500">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-black text-slate-800 tracking-tight">
                  {editingId ? '📝 Editando Questão' : '➕ Nova Questão'}
                </h2>
                {editingId && (
                  <button onClick={resetForms} className="text-xs font-bold text-red-500 uppercase tracking-widest">
                    Cancelar Edição
                  </button>
                )}
              </div>

              <form onSubmit={handleSaveQuestion} className="space-y-6 mb-16">
                <AdminInput
                  label="Título de Referência"
                  value={qForm.title || ''}
                  onChange={v => setQForm({ ...qForm, title: v })}
                  placeholder="Ex: VUP Básica Nível 1"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <AdminSelect
                    label="Tema Principal"
                    value={qForm.themeId || 1}
                    options={THEMES.map(t => ({ id: t.id, name: t.name }))}
                    onChange={v => setQForm({ ...qForm, themeId: parseInt(v), subtheme: '' })}
                  />
                  <AdminSelect
                    label="Subtema Específico"
                    value={qForm.subtheme || ''}
                    options={(SUBTHEMES_BY_THEME[qForm.themeId || 1] || []).map(s => ({ id: s, name: s }))}
                    onChange={v => setQForm({ ...qForm, subtheme: v })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                  <div className="md:col-span-2">
                    <AdminTextarea
                      label="Enunciado da Questão"
                      value={qForm.statement || ''}
                      onChange={v => setQForm({ ...qForm, statement: v })}
                    />
                  </div>

                  <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 space-y-4">
                    <AdminInput
                      label="URL da Imagem (Opcional)"
                      value={qForm.imageUrl || ''}
                      onChange={v => setQForm({ ...qForm, imageUrl: v })}
                      placeholder="https://..."
                    />

                    <div className="space-y-2">
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Ou Upload:
                      </label>
                      <input
                        ref={fileInputQuestion}
                        type="file"
                        accept="image/*"
                        onChange={e => handleFileUpload(e, 'imageUrl', 'q')}
                        className="text-xs text-slate-500"
                      />
                    </div>

                    {qForm.imageUrl && (
                      <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-white border border-slate-200">
                        <img src={qForm.imageUrl} alt="Preview Questão" className="w-full h-full object-contain" />
                        <button
                          type="button"
                          onClick={() => setQForm({ ...qForm, imageUrl: '' })}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full text-[8px] font-bold"
                        >
                          X
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-6 rounded-3xl border border-slate-100">
                  <AdminInput label="Alternativa A" value={qForm.optionA || ''} onChange={v => setQForm({ ...qForm, optionA: v })} />
                  <AdminInput label="Alternativa B" value={qForm.optionB || ''} onChange={v => setQForm({ ...qForm, optionB: v })} />
                  <AdminInput label="Alternativa C" value={qForm.optionC || ''} onChange={v => setQForm({ ...qForm, optionC: v })} />
                  <AdminInput label="Alternativa D" value={qForm.optionD || ''} onChange={v => setQForm({ ...qForm, optionD: v })} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <AdminSelect
                    label="Opção Correta"
                    value={qForm.correctOption || 'A'}
                    options={['A', 'B', 'C', 'D'].map(o => ({ id: o, name: o }))}
                    onChange={v => setQForm({ ...qForm, correctOption: v as any })}
                  />

                  <AdminSelect
                    label="Dificuldade do Card"
                    value={qForm.difficulty || 'moderado'}
                    options={['fácil', 'moderado', 'difícil', 'hard', 'desafio'].map(d => ({ id: d, name: d }))}
                    onChange={v => handleDifficultyChange(v as any)}
                  />
                </div>

                <RichTextField
                  label="Explicação / Gabarito Comentado"
                  value={String(qForm.explanation || '')}
                  onChange={html => setQForm({ ...qForm, explanation: html })}
                  placeholder="Digite a explicação..."
                />

                <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-2xl border border-amber-100">
                  <input
                    type="checkbox"
                    checked={!!qForm.isPremium}
                    onChange={e => setQForm({ ...qForm, isPremium: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-sm font-bold text-amber-800">Conteúdo Premium (Apenas assinantes)</span>
                </div>

                <button
                  type="submit"
                  disabled={isSavingQuestion}
                  className={`w-full py-6 rounded-2xl font-black text-xl shadow-xl transition-all ${
                    editingId ? 'bg-blue-600' : 'bg-slate-900'
                  } text-white ${isSavingQuestion ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {isSavingQuestion ? 'SALVANDO...' : editingId ? 'ATUALIZAR QUESTÃO' : 'CADASTRAR QUESTÃO'}
                </button>
              </form>

              <div className="border-t pt-10">
                <h3 className="font-black text-xl mb-6 text-slate-800">Banco de Dados de Questões</h3>

                <div className="bg-slate-50 p-6 rounded-3xl mb-8 flex gap-6 border border-slate-100">
                  <div className="flex-1">
                    <AdminSelect
                      label="Filtrar por Tema"
                      value={qListTheme}
                      options={THEMES.map(t => ({ id: t.id, name: t.name }))}
                      onChange={v => setQListTheme(parseInt(v))}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  {questions
                    .filter(q => q.themeId === qListTheme)
                    .map(q => (
                      <div
                        key={q.id}
                        className="p-5 bg-white border border-slate-100 rounded-2xl flex justify-between items-center group hover:border-blue-200 transition-all shadow-sm"
                      >
                        <div className="flex items-center gap-4">
                          <span className="text-[10px] font-black bg-slate-100 px-2 py-1 rounded text-slate-400">
                            ID: {q.id}
                          </span>
                          {q.imageUrl && (
                            <div className="w-10 h-10 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                              <img src={q.imageUrl} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div>
                            <div className="font-bold text-slate-800">{q.title}</div>
                            <div className="text-[10px] font-black text-blue-500 uppercase tracking-widest">
                              {q.subtheme} • {q.difficulty}
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => editItem('questoes', q)}
                            className="px-4 py-2 bg-slate-50 hover:bg-blue-50 text-blue-600 rounded-xl text-xs font-black transition-all"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteItem('questoes', q.id)}
                            className="px-4 py-2 bg-slate-50 hover:bg-red-50 text-red-500 rounded-xl text-xs font-black transition-all"
                          >
                            Excluir
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* ABA FLASHCARDS */}
          {activeTab === 'flashcards' && (
            <div className="animate-in fade-in duration-500">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-black text-slate-800 tracking-tight">
                  {editingId ? '🧠 Editando Flashcard' : '➕ Novo Flashcard'}
                </h2>
                {editingId && (
                  <button onClick={resetForms} className="text-xs font-bold text-red-500 uppercase tracking-widest">
                    Cancelar Edição
                  </button>
                )}
              </div>

              <form onSubmit={handleSaveFlashcard} className="space-y-6 mb-16">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <AdminSelect
                    label="Escolha o Deck (Tema)"
                    value={fForm.deckId || 1}
                    options={decks.map(d => ({ id: d.id, name: d.name }))}
                    onChange={v => setFForm({ ...fForm, deckId: parseInt(v), subtheme: '' })}
                  />
                  <AdminSelect
                    label="Subtema do Card"
                    value={fForm.subtheme || ''}
                    options={(SUBTHEMES_BY_THEME[currentThemeIdForSubthemes] || []).map(s => ({ id: s, name: s }))}
                    onChange={v => setFForm({ ...fForm, subtheme: v })}
                  />
                </div>

                {/* FRENTE e VERSO em tamanho real (sem preview) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                  <RichTextCardField
                    label="Frente (Pergunta)"
                    variant="front"
                    value={String(fForm.front || '')}
                    onChange={html => setFForm({ ...fForm, front: html })}
                    placeholder="Digite a frente do flashcard..."
                    imageUrl={String(fForm.frontImageUrl || '')}
                    onChangeImageUrl={url => setFForm({ ...fForm, frontImageUrl: url })}
                    onUploadImage={e => handleFileUpload(e, 'frontImageUrl', 'f')}
                    inputRef={fileInputFront}
                  />

                  <RichTextCardField
                    label="Verso (Resposta)"
                    variant="back"
                    value={String(fForm.back || '')}
                    onChange={html => setFForm({ ...fForm, back: html })}
                    placeholder="Digite o verso do flashcard..."
                    imageUrl={String(fForm.backImageUrl || '')}
                    onChangeImageUrl={url => setFForm({ ...fForm, backImageUrl: url })}
                    onUploadImage={e => handleFileUpload(e, 'backImageUrl', 'f')}
                    inputRef={fileInputBack}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSavingFlashcard}
                  className={`w-full py-6 bg-slate-900 text-white font-black text-xl rounded-2xl shadow-xl transition-all ${
                    isSavingFlashcard ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {isSavingFlashcard ? 'SALVANDO...' : editingId ? 'ATUALIZAR FLASHCARD' : 'CADASTRAR FLASHCARD'}
                </button>
              </form>

              <div className="border-t pt-10">
                <h3 className="font-black text-xl mb-6 text-slate-800">Flashcards Disponíveis</h3>

                <div className="bg-slate-50 p-6 rounded-3xl mb-8 border border-slate-100">
                  <AdminSelect
                    label="Filtrar por Deck"
                    value={fListDeck}
                    options={decks.map(d => ({ id: d.id, name: d.name }))}
                    onChange={v => setFListDeck(parseInt(v))}
                  />
                </div>

                <div className="space-y-3">
                  {flashcards.map(f => (
                    <div
                      key={f.id}
                      className="p-5 bg-white border border-slate-100 rounded-2xl flex justify-between items-center group shadow-sm"
                    >
                      <div className="flex-1 mr-6 flex items-center gap-4 min-w-0">
                        {f.frontImageUrl ? (
                          <div className="w-10 h-10 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={f.frontImageUrl} className="w-full h-full object-cover" />
                          </div>
                        ) : null}

                        <div className="min-w-0">
                          <div className="text-[10px] font-black text-blue-500 uppercase mb-1">{f.subtheme}</div>
                          <div className="font-bold text-slate-800 truncate">{stripHtml(String(f.front || ''))}</div>
                          <div className="text-[10px] font-black text-slate-400 uppercase mt-1">ID: {f.id}</div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => editItem('flashcards', f)}
                          className="px-4 py-2 bg-slate-50 text-blue-600 rounded-xl text-xs font-black transition-all"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteItem('flashcards', f.id)}
                          className="px-4 py-2 bg-slate-50 text-red-500 rounded-xl text-xs font-black transition-all"
                        >
                          Excluir
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ABA SIMULADOS */}
          {activeTab === 'simulados' && (
            <div className="animate-in fade-in duration-500">
              <h2 className="text-2xl font-black text-slate-800 mb-8 tracking-tight">
                {editingId ? '🧩 Editando Simulado' : '➕ Novo Simulado Oficial'}
              </h2>

              <form onSubmit={handleSaveSimulado} className="space-y-6 mb-16">
                <AdminInput label="Nome do Simulado" value={sForm.title || ''} onChange={v => setSForm({ ...sForm, title: v })} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <AdminSelect
                    label="Tema Base"
                    value={sForm.themeId || 1}
                    options={[...THEMES.map(t => ({ id: t.id, name: t.name })), { id: 99, name: '🎲 Aleatório' }]}
                    onChange={v => setSForm({ ...sForm, themeId: parseInt(v) })}
                  />
                  <AdminInput
                    label="Duração Estimada (min)"
                    value={String(sForm.durationMinutes || 60)}
                    onChange={v => setSForm({ ...sForm, durationMinutes: parseInt(v) })}
                  />
                </div>

                <AdminInput
                  label="IDs das Questões (Ex: 1, 15, 23)"
                  value={typeof sForm.questionIds === 'string' ? sForm.questionIds : (sForm.questionIds || []).join(', ')}
                  onChange={v => setSForm({ ...sForm, questionIds: v as any })}
                />

                <button
                  type="submit"
                  disabled={isSavingSimulado}
                  className={`w-full py-6 bg-slate-900 text-white font-black text-xl rounded-2xl shadow-xl transition-all ${
                    isSavingSimulado ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {isSavingSimulado ? 'SALVANDO...' : 'SALVAR SIMULADO'}
                </button>
              </form>

              <div className="border-t pt-10">
                <h3 className="font-black text-xl mb-8 text-slate-800">Simulados Oficiais</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {simulados.map(s => (
                    <div
                      key={s.id}
                      className="p-8 bg-slate-50 border border-slate-100 rounded-[2rem] flex flex-col gap-5 shadow-sm"
                    >
                      <h4 className="font-black text-slate-800 text-xl tracking-tight leading-none">{s.title}</h4>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => editItem('simulados', s)}
                          className="flex-1 py-3 bg-white text-blue-600 rounded-xl font-black"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteItem('simulados', s.id)}
                          className="flex-1 py-3 bg-white text-red-500 rounded-xl font-black"
                        >
                          Excluir
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ABA NOTÍCIAS */}
          {activeTab === 'atualizacoes' && (
            <div className="animate-in fade-in duration-500">
              <h2 className="text-2xl font-black text-slate-800 mb-8 tracking-tight">
                {editingId ? '📰 Editando Notícia' : '➕ Publicar Atualização'}
              </h2>

              <form onSubmit={handleSaveNews} className="space-y-6 mb-16">
                <AdminInput label="Título da Notícia" value={nForm.title || ''} onChange={v => setNForm({ ...nForm, title: v })} />

                <RichTextField
                  label="Resumo"
                  value={String(nForm.summary || '')}
                  onChange={html => setNForm({ ...nForm, summary: html })}
                  placeholder="Digite o resumo..."
                />

                <AdminTextarea label="Conteúdo" value={nForm.content || ''} onChange={v => setNForm({ ...nForm, content: v })} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <AdminSelect
                    label="Tema Principal"
                    value={nForm.themeId || 1}
                    options={THEMES.map(t => ({ id: t.id, name: t.name }))}
                    onChange={v => setNForm({ ...nForm, themeId: parseInt(v) })}
                  />

                  <div className="flex flex-col gap-4 p-5 bg-slate-50 rounded-2xl border border-slate-100">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!nForm.isFixed}
                        onChange={e => setNForm({ ...nForm, isFixed: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span className="text-sm font-bold text-slate-700">Fixar no topo do Feed</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!nForm.isPremium}
                        onChange={e => setNForm({ ...nForm, isPremium: e.target.checked })}
                        className="w-5 h-5"
                      />
                      <span className="text-sm font-bold text-slate-700">Notícia exclusiva Premium</span>
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSavingNews}
                  className={`w-full py-6 bg-slate-900 text-white font-black text-xl rounded-2xl shadow-xl transition-all ${
                    isSavingNews ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {isSavingNews ? 'PUBLICANDO...' : 'PUBLICAR NO FEED'}
                </button>
              </form>

              <div className="border-t pt-10">
                <h3 className="font-black text-xl mb-8 text-slate-800">Notícias Publicadas</h3>

                <div className="space-y-4">
                  {news.map(n => (
                    <div
                      key={n.id}
                      className="p-6 bg-white border border-slate-100 rounded-3xl flex justify-between items-center group shadow-sm"
                    >
                      <h4 className="font-bold text-slate-800">{n.title}</h4>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => editItem('atualizacoes', n)}
                          className="px-4 py-2 bg-slate-50 text-blue-600 rounded-xl text-[10px] font-black uppercase tracking-widest"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteItem('atualizacoes', n.id)}
                          className="px-4 py-2 bg-slate-50 text-red-500 rounded-xl text-[10px] font-black uppercase tracking-widest"
                        >
                          Excluir
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ================== UI Components ================== */

const TabButton = ({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
}) => (
  <button
    onClick={onClick}
    className={`px-8 py-4 rounded-[1.5rem] flex items-center gap-3 font-black text-sm transition-all border-2 ${
      active
        ? 'bg-slate-900 border-slate-900 text-white shadow-xl transform -translate-y-1'
        : 'bg-white text-slate-500 hover:bg-slate-50 border-slate-100'
    }`}
  >
    <span className="text-xl">{icon}</span> {label}
  </button>
);

const AdminInput = ({
  label,
  value,
  onChange,
  placeholder = '',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) => (
  <div className="space-y-2">
    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full p-4 bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-2xl transition-all font-bold text-slate-800 outline-none shadow-sm"
    />
  </div>
);

const AdminSelect = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | number;
  options: { id: string | number; name: string }[];
  onChange: (v: string) => void;
}) => (
  <div className="space-y-2">
    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">{label}</label>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full p-4 bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-2xl transition-all font-bold text-slate-800 outline-none shadow-sm appearance-none cursor-pointer"
    >
      <option value="">Selecione...</option>
      {options.map(o => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  </div>
);

const AdminTextarea = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="space-y-2">
    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">{label}</label>
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className="w-full p-5 bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-[2rem] h-40 transition-all font-bold text-slate-800 outline-none shadow-sm resize-none"
    />
  </div>
);

/**
 * AGORA: RichTextField com a MESMA TOOLBAR dos flashcards (sem imagem e sem card preview).
 * Mantive o nome RichTextField para você não precisar mexer no JSX.
 */
const RichTextField = ({
  label,
  value,
  onChange,
  placeholder = '',
}: {
  label: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const savedRangeRef = useRef<Range | null>(null);

  const FONTS = ['Arial', 'Arial Black', 'Helvetica', 'Impact', 'Times New Roman'];
  const SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48];

  const [fontFamily, setFontFamily] = useState<string>('Arial');
  const [fontSize, setFontSize] = useState<number>(14);

  const TEXT_COLORS = [
    { name: 'black', value: '#000000' },
    { name: 'red', value: '#dc2626' },
    { name: 'blue', value: '#2563eb' },
    { name: 'green', value: '#16a34a' },
  ];

  const HIGHLIGHTS = [
    { name: 'green', value: '#bbf7d0' },
    { name: 'yellow', value: '#fef08a' },
    { name: 'black', value: '#000000' },
    { name: 'red', value: '#fecaca' },
  ];

  const saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    savedRangeRef.current = sel.getRangeAt(0);
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (!sel || !savedRangeRef.current) return;
    sel.removeAllRanges();
    sel.addRange(savedRangeRef.current);
  };

  const sync = () => {
    const html = editorRef.current?.innerHTML ?? '';
    onChange(html);
  };

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = (command: string, val?: string) => {
    editorRef.current?.focus();
    restoreSelection();
    // eslint-disable-next-line deprecation/deprecation
    document.execCommand(command, false, val);
    saveSelection();
    sync();
  };

  const applyFontSizePx = (sizePx: number) => {
    editorRef.current?.focus();
    restoreSelection();

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    const span = document.createElement('span');
    span.style.fontSize = `${sizePx}px`;

    if (range.collapsed) {
      const textNode = document.createTextNode('\u200B');
      span.appendChild(textNode);
      range.insertNode(span);

      const newRange = document.createRange();
      newRange.setStart(textNode, 1);
      newRange.collapse(true);

      sel.removeAllRanges();
      sel.addRange(newRange);

      saveSelection();
      sync();
      return;
    }

    span.appendChild(range.extractContents());
    range.insertNode(span);

    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    newRange.collapse(false);
    sel.addRange(newRange);

    saveSelection();
    sync();
  };

  const applyHighlight = (color: string) => {
    exec('hiliteColor', color);
    exec('backColor', color);
  };

  const insertTable = (rows: number, cols: number) => {
    const r = Math.min(9, Math.max(1, rows));
    const c = Math.min(3, Math.max(1, cols));

    editorRef.current?.focus();
    restoreSelection();

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.margin = '10px 0';

    for (let i = 0; i < r; i++) {
      const tr = document.createElement('tr');
      for (let j = 0; j < c; j++) {
        const td = document.createElement('td');
        td.innerHTML = '&nbsp;';
        td.style.border = '1px solid #cbd5e1';
        td.style.padding = '8px';
        td.style.verticalAlign = 'top';
        tr.appendChild(td);
      }
      table.appendChild(tr);
    }

    range.insertNode(table);

    const after = document.createElement('p');
    after.innerHTML = '&nbsp;';
    table.parentNode?.insertBefore(after, table.nextSibling);

    const newRange = document.createRange();
    newRange.selectNodeContents(after);
    newRange.collapse(true);

    sel.removeAllRanges();
    sel.addRange(newRange);

    saveSelection();
    sync();
  };

  const handleInsertTable = () => {
    const r = Number(prompt('Linhas da tabela (1 a 9):', '3'));
    const c = Number(prompt('Colunas da tabela (1 a 3):', '3'));
    if (!r || !c) return;
    insertTable(r, c);
  };

  const getPlainTextFromHtml = (html: string) => {
    try {
      const doc = new DOMParser().parseFromString(html || '', 'text/html');
      return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    } catch {
      return (html || '').replace(/\s+/g, ' ').trim();
    }
  };

  const showPlaceholder = !getPlainTextFromHtml(value || '');

  return (
    <div className="space-y-3">
      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">{label}</label>

      {/* Toolbar completa (igual flashcards) */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-white rounded-2xl border border-slate-200 shadow-sm">
        <select
          value={fontFamily}
          onChange={(e) => {
            const next = e.target.value;
            setFontFamily(next);
            exec('fontName', next);
          }}
          className="h-10 rounded-xl bg-slate-50 border border-slate-200 px-3 text-sm font-bold text-slate-800"
          title="Fonte"
        >
          {FONTS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        <select
          value={fontSize}
          onChange={(e) => {
            const next = Number(e.target.value);
            setFontSize(next);
            applyFontSizePx(next);
          }}
          className="h-10 rounded-xl bg-slate-50 border border-slate-200 px-3 text-sm font-bold text-slate-800"
          title="Tamanho da fonte"
        >
          {SIZES.map((s) => (
            <option key={s} value={s}>
              {s}px
            </option>
          ))}
        </select>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => exec('bold')}
          className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 font-black"
          title="Negrito"
        >
          B
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => exec('italic')}
          className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 font-black italic"
          title="Itálico"
        >
          I
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => exec('underline')}
          className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 font-black underline"
          title="Sublinhado"
        >
          U
        </button>

        <div className="flex items-center gap-2 ml-1">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec('justifyLeft')}
            className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100"
            title="Alinhar à esquerda"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16 M4 10h10 M4 14h16 M4 18h10" />
            </svg>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec('justifyCenter')}
            className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100"
            title="Centralizar"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16 M6 10h12 M4 14h16 M6 18h12" />
            </svg>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec('justifyRight')}
            className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100"
            title="Alinhar à direita"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16 M10 10h10 M4 14h16 M10 18h10" />
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-2 ml-1">
          {TEXT_COLORS.map((c) => (
            <button
              key={c.name}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec('foreColor', c.value)}
              className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 flex items-center justify-center"
              title={`Text color: ${c.name}`}
            >
              <span className="w-4 h-4 rounded-full" style={{ backgroundColor: c.value }} />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-1">
          {HIGHLIGHTS.map((c) => (
            <button
              key={c.name}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyHighlight(c.value)}
              className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 flex items-center justify-center"
              title={`Background: ${c.name}`}
            >
              <span className="w-4 h-4 rounded-full border border-slate-300" style={{ backgroundColor: c.value }} />
            </button>
          ))}
        </div>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleInsertTable}
          className="h-10 px-4 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 font-black text-xs uppercase tracking-widest text-slate-700"
          title="Inserir tabela (máx 3 colunas x 9 linhas)"
        >
          Table
        </button>
      </div>

      {/* Editor */}
      <div className="relative w-full min-h-[220px] rounded-[2rem] border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="relative w-full h-full p-5 min-h-[220px]">
          {showPlaceholder && (
            <div className="pointer-events-none absolute top-5 left-5 text-slate-300 font-semibold">
              {placeholder}
            </div>
          )}

          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="w-full min-h-[220px] outline-none bg-transparent font-bold text-slate-800 text-left break-words [overflow-wrap:anywhere]"
            onInput={sync}
            onMouseUp={saveSelection}
            onKeyUp={saveSelection}
            onBlur={saveSelection}
          />
        </div>
      </div>
    </div>
  );
};

/* ================== Flashcard editor “tamanho real” ================== */

type CardVariant = 'front' | 'back';

const RichTextCardField = ({
  label,
  value,
  onChange,
  placeholder = '',
  variant,
  imageUrl,
  onChangeImageUrl,
  onUploadImage,
  inputRef,
}: {
  label: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  variant: CardVariant;
  imageUrl: string;
  onChangeImageUrl: (url: string) => void;
  onUploadImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const savedRangeRef = useRef<Range | null>(null);

  const FONTS = ['Arial', 'Arial Black', 'Helvetica', 'Impact', 'Times New Roman'];
  const SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48];

  const [fontFamily, setFontFamily] = useState<string>('Arial');
  const [fontSize, setFontSize] = useState<number>(14);

  const TEXT_COLORS = [
    { name: 'black', value: '#000000' },
    { name: 'red', value: '#dc2626' },
    { name: 'blue', value: '#2563eb' },
    { name: 'green', value: '#16a34a' },
  ];

  const HIGHLIGHTS = [
    { name: 'green', value: '#bbf7d0' },
    { name: 'yellow', value: '#fef08a' },
    { name: 'black', value: '#000000' },
    { name: 'red', value: '#fecaca' },
  ];

  const saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    savedRangeRef.current = sel.getRangeAt(0);
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (!sel || !savedRangeRef.current) return;
    sel.removeAllRanges();
    sel.addRange(savedRangeRef.current);
  };

  const sync = () => {
    const html = editorRef.current?.innerHTML ?? '';
    onChange(html);
  };

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = (command: string, val?: string) => {
    editorRef.current?.focus();
    restoreSelection();
    // eslint-disable-next-line deprecation/deprecation
    document.execCommand(command, false, val);
    saveSelection();
    sync();
  };

  const applyFontSizePx = (sizePx: number) => {
    editorRef.current?.focus();
    restoreSelection();

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    const span = document.createElement('span');
    span.style.fontSize = `${sizePx}px`;

    if (range.collapsed) {
      const textNode = document.createTextNode('\u200B');
      span.appendChild(textNode);
      range.insertNode(span);

      const newRange = document.createRange();
      newRange.setStart(textNode, 1);
      newRange.collapse(true);

      sel.removeAllRanges();
      sel.addRange(newRange);

      saveSelection();
      sync();
      return;
    }

    span.appendChild(range.extractContents());
    range.insertNode(span);

    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    newRange.collapse(false);
    sel.addRange(newRange);

    saveSelection();
    sync();
  };

  const applyHighlight = (color: string) => {
    exec('hiliteColor', color);
    exec('backColor', color);
  };

  const insertTable = (rows: number, cols: number) => {
    const r = Math.min(9, Math.max(1, rows));
    const c = Math.min(3, Math.max(1, cols));

    editorRef.current?.focus();
    restoreSelection();

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.margin = '10px 0';

    for (let i = 0; i < r; i++) {
      const tr = document.createElement('tr');
      for (let j = 0; j < c; j++) {
        const td = document.createElement('td');
        td.innerHTML = '&nbsp;';
        td.style.border = '1px solid #cbd5e1';
        td.style.padding = '8px';
        td.style.verticalAlign = 'top';
        tr.appendChild(td);
      }
      table.appendChild(tr);
    }

    range.insertNode(table);

    const after = document.createElement('p');
    after.innerHTML = '&nbsp;';
    table.parentNode?.insertBefore(after, table.nextSibling);

    const newRange = document.createRange();
    newRange.selectNodeContents(after);
    newRange.collapse(true);

    sel.removeAllRanges();
    sel.addRange(newRange);

    saveSelection();
    sync();
  };

  const handleInsertTable = () => {
    const r = Number(prompt('Linhas da tabela (1 a 9):', '3'));
    const c = Number(prompt('Colunas da tabela (1 a 3):', '3'));
    if (!r || !c) return;
    insertTable(r, c);
  };

  const plainText = (editorRef.current?.textContent || '').replace(/\s+/g, ' ').trim();
  const showPlaceholder = !plainText;

  const cardBg = variant === 'back' ? 'bg-blue-50 border-blue-100' : 'bg-white border-slate-100';
  const textClass =
    variant === 'back'
      ? 'text-[clamp(1.05rem,2.2vw,1.7rem)] leading-relaxed'
      : 'text-[clamp(1.15rem,2.6vw,1.9rem)] leading-tight';

  return (
    <div className="space-y-3">
      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">
        {label}
      </label>

      {/* Toolbar completa */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-white rounded-2xl border border-slate-200 shadow-sm">
        <select
          value={fontFamily}
          onChange={e => {
            const next = e.target.value;
            setFontFamily(next);
            exec('fontName', next);
          }}
          className="h-10 rounded-xl bg-slate-50 border border-slate-200 px-3 text-sm font-bold text-slate-800"
          title="Fonte"
        >
          {FONTS.map(f => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        <select
          value={fontSize}
          onChange={e => {
            const next = Number(e.target.value);
            setFontSize(next);
            applyFontSizePx(next);
          }}
          className="h-10 rounded-xl bg-slate-50 border border-slate-200 px-3 text-sm font-bold text-slate-800"
          title="Tamanho da fonte"
        >
          {SIZES.map(s => (
            <option key={s} value={s}>
              {s}px
            </option>
          ))}
        </select>

        <button
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={() => exec('bold')}
          className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 font-black"
        >
          B
        </button>
        <button
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={() => exec('italic')}
          className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 font-black italic"
        >
          I
        </button>
        <button
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={() => exec('underline')}
          className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 font-black underline"
        >
          U
        </button>

        <div className="flex items-center gap-2 ml-1">
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => exec('justifyLeft')}
            className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100"
            title="Alinhar esquerda"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 6h16 M4 10h10 M4 14h16 M4 18h10" />
            </svg>
          </button>
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => exec('justifyCenter')}
            className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100"
            title="Centralizar"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 6h16 M6 10h12 M4 14h16 M6 18h12" />
            </svg>
          </button>
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => exec('justifyRight')}
            className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100"
            title="Alinhar direita"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 6h16 M10 10h10 M4 14h16 M10 18h10" />
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-2 ml-1">
          {TEXT_COLORS.map(c => (
            <button
              key={c.name}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => exec('foreColor', c.value)}
              className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 flex items-center justify-center"
              title={`Text color: ${c.name}`}
            >
              <span className="w-4 h-4 rounded-full" style={{ backgroundColor: c.value }} />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-1">
          {HIGHLIGHTS.map(c => (
            <button
              key={c.name}
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={() => applyHighlight(c.value)}
              className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 flex items-center justify-center"
              title={`Background: ${c.name}`}
            >
              <span
                className="w-4 h-4 rounded-full border border-slate-300"
                style={{ backgroundColor: c.value }}
              />
            </button>
          ))}
        </div>

        <button
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={handleInsertTable}
          className="h-10 px-4 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 font-black text-xs uppercase tracking-widest text-slate-700"
          title="Inserir tabela (máx 3 colunas x 9 linhas)"
        >
          Table
        </button>
      </div>

      {/* Imagem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AdminInput
          label={variant === 'front' ? 'URL Imagem Frente' : 'URL Imagem Verso'}
          value={imageUrl}
          onChange={onChangeImageUrl}
          placeholder="https://..."
        />
        <div className="space-y-2">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">
            Ou Upload
          </label>
          <input ref={inputRef} type="file" accept="image/*" onChange={onUploadImage} className="text-xs" />
        </div>
      </div>

      {/* Card editor em tamanho real */}
      <div
        className={`relative w-full aspect-[4/3] sm:aspect-[16/10] rounded-[2rem] shadow-lg border ${cardBg} overflow-hidden`}
      >
        <div className="w-full h-full flex flex-col p-6 sm:p-10">
          {imageUrl ? (
            <div className="mb-4 max-h-[45%] w-full flex items-center justify-center overflow-hidden rounded-2xl flex-shrink-0">
              <img src={imageUrl} alt="Imagem" className="max-w-full max-h-full object-contain" />
            </div>
          ) : null}

          <div className="relative flex-1 min-h-0 overflow-auto">
            {showPlaceholder && (
              <div className="pointer-events-none absolute top-0 left-0 text-slate-300 font-semibold">
                {placeholder}
              </div>
            )}

            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              className={`w-full h-full outline-none bg-transparent font-bold text-slate-800 text-left break-words [overflow-wrap:anywhere] ${textClass}`}
              onInput={sync}
              onPaste={(e) => {
                // Correção: cola somente texto puro (evita HTML/charset estranho que pode travar o salvar)
                e.preventDefault();
                const text = e.clipboardData?.getData('text/plain') ?? '';
                // eslint-disable-next-line deprecation/deprecation
                document.execCommand('insertText', false, text);
                saveSelection();
                sync();
              }}
              onMouseUp={saveSelection}
              onKeyUp={saveSelection}
              onBlur={saveSelection}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
